import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import QRCodeStyling from 'qr-code-styling';
import { COLOR_BACKGROUND_KEY, COLOR_FOREGROUND_KEY } from '../../constants/storageKeys';
import ColorField from '../common/ColorField';
import { useLocalStorageState } from '../../utils/storage';
import { normalizeHex } from '../../utils/color';

function QRToolPage({ tool }) {
  const containerRef = useRef(null);
  const qrInstanceRef = useRef(null);
  const [qrColor, setQrColor] = useLocalStorageState(COLOR_FOREGROUND_KEY, '#000000');
  const [bgColor, setBgColor] = useLocalStorageState(COLOR_BACKGROUND_KEY, '#ffffff');
  const [transparent, setTransparent] = useState(false);
  const [copied, setCopied] = useState(false);

  const initialValues = useMemo(() => {
    const values = {};
    tool.fields.forEach((field) => {
      values[field.key] = field.defaultValue ?? (field.type === 'checkbox' ? false : '');
    });
    return values;
  }, [tool]);

  const [values, setValues] = useState(initialValues);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  useEffect(() => {
    if (!qrInstanceRef.current) {
      qrInstanceRef.current = new QRCodeStyling({
        width: 260,
        height: 260,
        type: 'svg',
        data: ' ',
        dotsOptions: { color: '#000000' },
        backgroundOptions: { color: '#ffffff' },
      });
    }
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
      qrInstanceRef.current.append(containerRef.current);
    }
  }, []);

  const outputData = useMemo(() => tool.build(values), [tool, values]);

  useEffect(() => {
    if (!qrInstanceRef.current) return;
    const dotsColor = normalizeHex(qrColor, '#000000');
    const bg = transparent ? 'transparent' : normalizeHex(bgColor, '#ffffff');
    qrInstanceRef.current.update({
      data: outputData || ' ',
      dotsOptions: { color: dotsColor },
      backgroundOptions: { color: bg },
    });
  }, [outputData, qrColor, bgColor, transparent]);

  const handleFieldChange = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const download = (extension) => {
    if (!qrInstanceRef.current) return;
    qrInstanceRef.current.download({ extension });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(outputData || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {tool.label}
            </Typography>
            <Typography color="text.secondary">QR Generator</Typography>
            {tool.description && (
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                {tool.description}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" onClick={() => download('png')}>Download PNG</Button>
            <Button variant="outlined" onClick={() => download('svg')}>Download SVG</Button>
          </Stack>
        </Stack>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={2}>
              {tool.fields.map((field) => (
                <Box key={field.key}>
                  {field.type === 'textarea' && (
                    <TextField
                      fullWidth
                      label={field.label}
                      placeholder={field.placeholder}
                      value={values[field.key]}
                      multiline
                      minRows={3}
                      onChange={(event) => handleFieldChange(field.key, event.target.value)}
                    />
                  )}
                  {field.type === 'select' && (
                    <FormControl fullWidth>
                      <InputLabel>{field.label}</InputLabel>
                      <Select
                        label={field.label}
                        value={values[field.key]}
                        onChange={(event) => handleFieldChange(field.key, event.target.value)}
                      >
                        {field.options.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {field.type === 'checkbox' && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(values[field.key])}
                          onChange={(event) => handleFieldChange(field.key, event.target.checked)}
                        />
                      }
                      label={field.label}
                    />
                  )}
                  {field.type !== 'textarea' && field.type !== 'select' && field.type !== 'checkbox' && (
                    <TextField
                      fullWidth
                      label={field.label}
                      type={field.type}
                      placeholder={field.placeholder}
                      value={values[field.key]}
                      onChange={(event) => handleFieldChange(field.key, event.target.value)}
                      InputLabelProps={field.type === 'datetime-local' ? { shrink: true } : undefined}
                    />
                  )}
                </Box>
              ))}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ColorField
                    label="Foreground Color (hex)"
                    placeholder="#000000"
                    value={qrColor}
                    onChange={setQrColor}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ColorField
                    label="Background Color (hex)"
                    placeholder="#ffffff"
                    value={bgColor}
                    onChange={setBgColor}
                    disabled={transparent}
                  />
                </Grid>
              </Grid>
              <FormControlLabel
                control={
                  <Switch
                    checked={transparent}
                    onChange={(event) => setTransparent(event.target.checked)}
                  />
                }
                label="Transparent background"
              />
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Stack spacing={2}>
              <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>Preview</Typography>
                <Box
                  ref={containerRef}
                  sx={{
                    minHeight: 280,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: 2,
                    background:
                      'repeating-linear-gradient(45deg, rgba(0,0,0,0.06), rgba(0,0,0,0.06) 12px, transparent 12px, transparent 24px)',
                  }}
                />
              </Paper>
              <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>Output data</Typography>
                  <Button variant="outlined" size="small" onClick={handleCopy}>Copy</Button>
                </Stack>
                <Typography
                  component="div"
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.9rem',
                    color: 'text.secondary',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {outputData || 'Start typing to generate data.'}
                </Typography>
                {copied && (
                  <Typography sx={{ mt: 1, fontWeight: 600 }} color="success.main">
                    Copied to clipboard.
                  </Typography>
                )}
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  );
}

export default QRToolPage;
